import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import reviewStyles from './writeReview-tw-styles';
import constants from '../../../constants/messages/pt-BR';

class WriteReviewRating extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hover: 0,
    };
    this.errorRef = React.createRef();
  }

  render() {
    const { formData, type, onChange, validateField } = this.props;
    const { hover } = this.state;
    
    const radioOnClick = e => {
      onChange(e.target.name, e.target.value);
      if (e.target.name === 'recommend' || e.target.name === 'overallRating') {
        validateField(e.target.name);
      }
    };

    const handleBlur = (e) => {
      const fieldName = formData[type.unique_name].name;
      if (fieldName) {
        // Use relatedTarget to check where focus is moving to
        const relatedTarget = e.relatedTarget;
        const radiogroup = e.currentTarget;
        
        // Only validate if focus is moving outside the radiogroup
        if (!relatedTarget || !radiogroup.contains(relatedTarget)) {
          validateField(fieldName);
          // Focus the error message if validation fails
          setTimeout(() => {
            if (formData[type.unique_name].error && this.errorRef.current) {
              this.errorRef.current.focus();
            }
          }, 50);
        }
      }
    };

    const handleLabelKeyDown = (e, inputId, starValue) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        const input = document.getElementById(inputId);
        if (input) {
          input.click();
        }
      } else if (e.key === 'Tab' && !e.shiftKey && starValue === 5) {
        // On the last star, validate before allowing tab
        const fieldName = formData[type.unique_name].name;
        if (fieldName && !formData[type.unique_name].value) {
          e.preventDefault();
          validateField(fieldName);
          setTimeout(() => {
            if (this.errorRef.current) {
              this.errorRef.current.focus();
            }
          }, 50);
        }
      }
    };

    const handleMouseEnter = (value) => {
      this.setState({ hover: value });
    };

    const handleMouseLeave = () => {
      this.setState({ hover: 0 });
    };

    const currentValue = parseInt(formData[type.unique_name].value) || 0;
    const activeValue = hover || currentValue;

    const reviewRatingType =
      type.ratingType === 'starRating'
        ? { ratingType: 'starRating', radioType: 'primaryRadio' }
        : { ratingType: 'hyphenRating', radioType: 'secondaryRadio' };
    return (
      <div className={classNames(reviewStyles.reviewForm.formSection)}>
        <div className={classNames(reviewStyles.reviewForm.labelContainer)}>
          <label
            className={classNames(reviewStyles.reviewForm.label)}
            id={`${type.unique_name}-label`}
            tabIndex={0}
          >
            {
              <FormattedMessage
                id={type.label}
                defaultMessage={constants.ratingLabel}
              />
            }
            <span className="mandatory_field_star">*</span>
          </label>
        </div>
        <div className={classNames(reviewStyles.reviewForm.ratingContainer)}>
          <p
            className={classNames(
              reviewStyles.reviewForm[reviewRatingType.ratingType]
            )}
          >
            <div
              role="radiogroup"
              aria-labelledby={`${type.unique_name}-label`}
              className={classNames(reviewStyles.reviewForm.floatLeft)}
              onBlur={handleBlur}
            >
              {[1, 2, 3, 4, 5].map(starValue => {
                const isSelected = currentValue === starValue;
                const starText = `${starValue} estrela${
                  starValue > 1 ? 's' : ''
                }`;
                const selectionState = isSelected ? 'selecionado' : 'não selecionado';
                return (
                  <React.Fragment key={starValue}>
                    <input
                      type="radio"
                      id={`${type.unique_name}${starValue}`}
                      name={formData[type.unique_name].name}
                      onChange={radioOnClick}
                      value={starValue}
                      checked={isSelected}
                      aria-label={`${starText}, ${selectionState}`}
                    />
                    <label
                      className={classNames(
                        reviewStyles.reviewForm.primaryLabel,
                        { 'star-filled': starValue <= activeValue }
                      )}
                      htmlFor={`${type.unique_name}${starValue}`}
                      onMouseEnter={() => handleMouseEnter(starValue)}
                      onMouseLeave={handleMouseLeave}
                      tabIndex={0}
                      aria-label={`${starText}, ${selectionState}`}
                      onKeyDown={(e) => handleLabelKeyDown(e, `${type.unique_name}${starValue}`, starValue)}
                    >
                      <span className="sr-only">{`${starText}, ${selectionState}`}</span>
                    </label>
                  </React.Fragment>
                );
              })}
            </div>
            {formData[type.unique_name].error && (
              <p 
                ref={this.errorRef}
                className={classNames(reviewStyles.reviewForm.errorText)}
                role="alert"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Tab' && !e.shiftKey) {
                    // Allow tab to move forward naturally
                    return;
                  }
                }}
              >
                <FormattedMessage
                  id="overallRatingRequiredError"
                  defaultMessage={constants.overallRatingRequiredError}
                />
              </p>
            )}
          </p>
        </div>
      </div>
    );
  }
}
export default observer(WriteReviewRating);

WriteReviewRating.propTypes = {
  //  formData
  formData: PropTypes.oneOfType([PropTypes.object]),

  //   type of rating
  type: PropTypes.oneOfType([PropTypes.object]),

  //   onChange function mobx
  onChange: PropTypes.func,

  // validate function mobx
  validateField: PropTypes.func,
};

// TODO(next16): defaultProps removed in React 19 for function components.
// Convert to inline parameter defaults: const WriteReviewRating = ({prop = val, formData = { }) =>
WriteReviewRating.defaultProps = {
  formData: {},
  type: {},
  onChange: () => {},
  validateField: () => {},
};